import { isVpnMode, playListUrl } from "../../state";
//@ts-ignore
import { createEffect } from "solid-js";
import { t } from "../translations/configuration";
import { getProxyHeaders, getProxyUrl } from "./proxyHeaders";
import { isProxyHealthOk } from "../pages/proxyHealth/proxyHealthCall";

let API_BASE = "";

createEffect(() => {
  API_BASE = isVpnMode() ? playListUrl() : playListUrl();
});

// Watch for changes in playListUrl and update API_BASE accordingly
createEffect(() => {
  const data = playListUrl();
  API_BASE = data;
});

const basePosterSize = "w185";

export function getImageUrl(path: string, posterSize: string = basePosterSize) {
  return posterSize + path;
}

export function getImageUrlAbsolute(path: string = basePosterSize) {
  const str = typeof path === "string" ? path : String(path || "");
  if (str && (str.startsWith("http://") || str.startsWith("https://"))) {
    const encodedUrl = btoa(
      Array.from(new TextEncoder().encode(str), b => String.fromCharCode(b)).join('')
    );
    return `${getProxyUrl()}${encodedUrl}`;
  }
  return str;
}

// Full catalog downloads (get_live_streams / get_vod_streams / get_series) can be
// tens of MB and are fetched three at a time on the Home screen. Panels also tend
// to build the whole JSON before sending any headers, so both the wait for headers
// and the download get a far larger budget than ordinary API calls.
export const BULK_TIMEOUT_MS = 180000;

// Even a single category's stream list can be several MB; on a Tizen TV going
// through the proxy that has been seen to take longer than 20s to arrive after
// the headers were already back (Sentry: AbortError on a category fetch that
// had returned 200 headers), so the body gets much more room than the connect.
const DEFAULT_BODY_TIMEOUT_MS = 60000;

// `timeoutMs` covers connecting and receiving the response headers; the timer is
// cleared as soon as they arrive. `bodyTimeoutMs` is a separate budget for
// downloading/parsing the body, so a slow-but-alive transfer of a large payload
// isn't killed by the short header timeout. A value <= 0 disables that timer.
async function _get(
  path: string,
  params: RequestInit = {},
  timeoutMs: number = 20000,
  bodyTimeoutMs: number = DEFAULT_BODY_TIMEOUT_MS
) {
  const fullUrl = API_BASE + path;

  // UTF-8 safe base64 (plain btoa throws on non-Latin-1 characters)
  const encodedUrl = btoa(
    Array.from(new TextEncoder().encode(fullUrl), b => String.fromCharCode(b)).join("")
  );

  const proxyUrl = localStorage.getItem("proxy_url");

  // Construct final URL based on vpnMode
  const finalUrl = isVpnMode() ? `${proxyUrl}${encodedUrl}` : fullUrl;

  if (isVpnMode()) {
    const isHealthy = await isProxyHealthOk(t);
    if (!isHealthy) return false; // stop if proxy is down
  }

  // TV connections can stall indefinitely; without this, a dropped
  // connection never rejects and the caller waits forever.
  // AbortController doesn't exist on Chromium < 66 (Tizen 5.0 and older ship
  // 63/56/47) and isn't covered by the legacy polyfills, so constructing it
  // unguarded throws a ReferenceError and fails every request on those TVs.
  // Without it we simply run untimed, as before.
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timeoutId = controller && timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;

  try {
    const response = await fetch(finalUrl, {
      ...params,
      headers: {
        ...(params.headers || {}),
        ...(isVpnMode() ? getProxyHeaders() : {}),
      },
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    // Headers are in — switch from the connect timeout to the body budget.
    clearTimeout(timeoutId);
    timeoutId = controller && bodyTimeoutMs > 0 ? setTimeout(() => controller.abort(), bodyTimeoutMs) : undefined;

    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

// Retries transient failures (timeouts, dropped connections) a few times
// with backoff before giving up, so a single flaky request on a slow TV
// network doesn't silently turn into "no channels/movies/series loaded".
async function _getWithRetry(
  path: string,
  params: RequestInit = {},
  retries: number = 2,
  timeoutMs: number = 20000,
  bodyTimeoutMs: number = DEFAULT_BODY_TIMEOUT_MS
) {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await _get(path, params, timeoutMs, bodyTimeoutMs);
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

export default {
  _get,
  _getWithRetry,
};